import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  IconButton,
  Collapse,
  useDisclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  DragHandleIcon,
  SearchIcon,
  StarIcon,
  ChevronDownIcon,
  AddIcon,
  EditIcon,
} from '@chakra-ui/icons';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { AppDispatch } from 'utils/GlobalHelpers';
import {
  getDashboard,
  getSelectedPage,
  setSelectedPage,
  openPageModal,
  reorderPages,
  updatePageOrder,
  deletePage,
} from 'redux/DashboardSliceV2';
import InfoIconComponent from 'components/Icons/InfoIcon';
import DeleteModal from 'components/Modals/DeleteModal';

const PageBar = () => {
  const dispatch: AppDispatch = useDispatch();
  const dashboard = useSelector(getDashboard);
  const [menu, setMenu]: any = useState('');
  const selectedPage = useSelector(getSelectedPage);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageToDelete, setPageToDelete] = useState<any>(null);
  const { isOpen: isFavoritesOpen, onToggle: toggleFavorites } = useDisclosure({
    defaultIsOpen: true,
  });
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();
  // const { isOpen: isAllPagesOpen, onToggle: toggleAllPages } = useDisclosure({
  //   defaultIsOpen: true,
  // });

  const pages = dashboard?.pages || [];
  const favoritePages = pages.filter((page: any) => page.is_favorite);

  const filteredPages = pages.filter((page: any) =>
    page.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const setSelectedPageAndPersist = (page: any) => {
    dispatch(setSelectedPage(page));
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[dashboard.model_id] = {
      ...data[dashboard.model_id],
      selectedPageId: page?.id,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  };
  const handlePageSelect = (page: any) => {
    setSelectedPageAndPersist(page);
  };

  const handleCreatePage = () => {
    dispatch(openPageModal({}));
  };

  const handleEditPage = (page: any) => {
    dispatch(openPageModal(page));
  };

  const handleDeletePage = (page: any) => {
    setPageToDelete(page);
    openDeleteModal();
  };

  const confirmDeletePage = () => {
    if (pageToDelete) {
      dispatch(deletePage({ pageId: pageToDelete.id }));
      setPageToDelete(null);
      closeDeleteModal();
    }
  };

  const cancelDeletePage = () => {
    setPageToDelete(null);
    closeDeleteModal();
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Work with the currently displayed sorted pages (ascending order - same as UI)
    const displayedPages = [...filteredPages].sort((a: any, b: any) => a.position - b.position);

    // Create a copy and reorder based on visual order
    const reorderedPages = Array.from(displayedPages);
    const [movedPage] = reorderedPages.splice(sourceIndex, 1);
    reorderedPages.splice(destinationIndex, 0, movedPage);

    // Assign new positions in ascending order to match the display
    const updatedPages = reorderedPages.map((page, index) => ({
      ...(page as any),
      position: index, // Start from 0 and increment for ascending sort
    }));

    // Update all pages (filtered + non-filtered)
    const nonFilteredPages = pages.filter(
      (page: any) => !filteredPages.some((fp: any) => fp.id === page.id),
    );
    const allUpdatedPages = [...nonFilteredPages, ...updatedPages];

    // Update local state immediately
    dispatch(reorderPages(allUpdatedPages));

    try {
      // Prepare dashboard object with correct structure
      const updatedDashboard = {
        ...dashboard,
        pages: allUpdatedPages,
      };
      await dispatch(updatePageOrder({ dashboard: updatedDashboard }));
    } catch {
      // Revert to original order if update fails
      dispatch(reorderPages(pages));
    }
  };

  const handleMenuClick = (event: any) => {
    event.stopPropagation();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        width={{ base: '340px', md: '360px', lg: '390px' }}
        flexShrink={0}
        height='100%'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        color='white'
        borderRight='2px solid #FFFFFF15'
        px={3}
      >
        <Box p={4} pt={2} flexShrink={0}>
          <Flex justify={'space-between'} align='center'>
            <Text fontSize='xl' fontFamily={'bold_roboto'} fontWeight={'semibold'}>
              Pages
            </Text>
            <IconButton
              aria-label='Create Page'
              onClick={handleCreatePage}
              _hover={{
                bg: 'rgba(255, 255, 255, 0.2)',
              }}
              color='white'
              _active={{
                bg: 'rgba(255, 255, 255, 0.3)',
              }}
              variant='clear'
              size='auto'
              fontSize='lg'
              icon={<AddIcon />}
            ></IconButton>
          </Flex>
        </Box>
        <Box px={4} flex='1' minH='0' display='flex' flexDirection='column'>
          <InputGroup size='md' mb={4} flexShrink={0}>
            <InputLeftElement pointerEvents='none'>
              <SearchIcon color='white' opacity={0.7} />
            </InputLeftElement>
            <Input
              placeholder='Search'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              bg='rgba(255, 255, 255, 0.1)'
              border='1px solid rgba(255, 255, 255, 0.2)'
              borderRadius='8px'
              color='white'
              _placeholder={{ color: 'rgba(255, 255, 255, 0.7)' }}
              _focus={{
                borderColor: 'rgba(255, 255, 255, 0.4)',
                boxShadow: 'none',
              }}
            />
          </InputGroup>

          {/* Favorite Pages Section */}
          {favoritePages.length > 0 && (
            <Box mb={6} flexShrink={0}>
              <Flex
                alignItems='center'
                justifyContent='space-between'
                cursor='pointer'
                onClick={toggleFavorites}
                mb={2}
              >
                <Flex alignItems='center'>
                  <StarIcon mr={2} boxSize={4} />
                  <Text fontWeight='semibold' fontSize='sm'>
                    Favourite Pages
                  </Text>
                </Flex>
                <ChevronDownIcon
                  boxSize={5}
                  transform={isFavoritesOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                  transition='transform 0.2s'
                />
              </Flex>
              <Collapse in={isFavoritesOpen}>
                <VStack align='stretch' spacing={1} pl={4}>
                  {favoritePages.map((page: any) => (
                    <Flex
                      key={page.id}
                      p={2}
                      borderRadius='6px'
                      cursor='pointer'
                      bg={selectedPage?.id === page.id ? 'rgba(255, 255, 255, 0.2)' : 'transparent'}
                      _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                      alignItems='center'
                      justifyContent='space-between'
                      role='group'
                    >
                      <Box onClick={() => handlePageSelect(page)} flex={1}>
                        <Text fontSize='sm' noOfLines={1}>
                          {page.name || `Page ${page.id}`}
                        </Text>
                      </Box>
                      <EditIcon
                        boxSize={3}
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        transition='opacity 0.2s'
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditPage(page);
                        }}
                      />
                    </Flex>
                  ))}
                </VStack>
              </Collapse>
            </Box>
          )}

          {/* All Pages Section — flex='1' so it fills remaining sidebar height,
               bg/borderRadius/padding/scroll promoted here from the inner VStack */}
          <Box
            flex='1'
            minH='0'
            zIndex={menu ? 5 : 'auto'}
            bg='#7163D0'
            borderRadius='15px'
            overflowY='auto'
            className='scroll'
            padding={3}
          >
            <Collapse in={true}>
              <Droppable droppableId='pages-list'>
                {(provided) => (
                  <VStack
                    align='stretch'
                    spacing={1}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {filteredPages
                      .sort((a: any, b: any) => a.position - b.position)
                      .map((page: any, index: number) => (
                        <Draggable key={page.id} draggableId={page.id.toString()} index={index}>
                          {(dragProvided, snapshot) => (
                            <Flex
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              key={page.id}
                              p={2}
                              px={4}
                              borderRadius='15px'
                              cursor='pointer'
                              bg={
                                snapshot.isDragging
                                  ? 'rgba(255, 255, 255, 0.3)'
                                  : selectedPage?.id === page.id
                                  ? '#4BB6B9'
                                  : 'transparent'
                              }
                              _hover={{ bg: '#4BB6B9', cursor: 'pointer' }}
                              alignItems='center'
                              justifyContent='space-between'
                              role='group'
                              transition='background-color 0.2s'
                            >
                              <Flex
                                alignItems='center'
                                justifyContent='space-between'
                                flex={1}
                                onClick={() => handlePageSelect(page)}
                                title={`${page.name || `Page ${page.id}`}`}
                              >
                                <Box>
                                  <Text fontSize='sm' fontWeight='medium' noOfLines={1}>
                                    {index + 1}. {page.name || `Page ${page.id}`}
                                  </Text>
                                  {page?.description && (
                                    <Text fontSize='xs' opacity={0.8} noOfLines={1}>
                                      {page?.description}
                                    </Text>
                                  )}
                                </Box>
                                <DragHandleIcon
                                  boxSize={4}
                                  display='none'
                                  _groupHover={{ display: 'block' }}
                                  alignItems='center'
                                  ml={2}
                                />
                              </Flex>
                              <Menu
                                closeOnSelect={true}
                                onOpen={() => setMenu(page?.id)}
                                onClose={() => setMenu('')}
                                variant='primary'
                                placement='bottom-start'
                              >
                                <MenuButton
                                  as={Box}
                                  onClick={(event: any) => handleMenuClick(event)}
                                  boxSize={4}
                                  display='none'
                                  alignContent='center'
                                  _groupHover={{ display: 'block' }}
                                  ml={2}
                                  cursor='pointer'
                                >
                                  <InfoIconComponent color='white' width='32' height='8' />
                                </MenuButton>
                                <MenuList minWidth={'160px !important'} zIndex={5}>
                                  <MenuItem
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleEditPage(page);
                                    }}
                                  >
                                    Edit
                                  </MenuItem>
                                  {pages?.length > 1 && (
                                    <MenuItem
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeletePage(page);
                                      }}
                                    >
                                      Delete
                                    </MenuItem>
                                  )}
                                </MenuList>
                              </Menu>
                            </Flex>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </VStack>
                )}
              </Droppable>
            </Collapse>
          </Box>
        </Box>
      </Box>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeletePage}
        itemName={pageToDelete?.name || `Page ${pageToDelete?.id}`}
        onDelete={confirmDeletePage}
      />
    </DragDropContext>
  );
};

export default withLDConsumer()(PageBar);
